import { useHistory } from "react-router-dom";
import { getNavigateToPlatform } from "../mfe/navigationBridge";
import redirectToStudentPortal from "../Core/utils/redirectToStudentPortal";

/**
 * Context-aware navigation hook for SPDashboard components.
 * Detects whether Sandbox is embedded inside StudentPortal or running standalone,
 * and routes to the correct destination based on the current mode.
 *
 * --- Target formats ---
 *   "sp:<path>"   StudentPortal route. In embedded mode calls the portal's onNavigate;
 *                 in standalone mode redirects to StudentPortal through
 *                 redirectToStudentPortal, which carries the session across.
 *   "sb:<path>"   Sandbox route. Always uses history.push via react-router.
 *   "link:<url>"  External URL. Opens in a new tab by default.
 *   "manual"      Calls the handler passed as the second argument (see below).
 *
 * --- Handler argument (for "manual" targets) ---
 *   A plain function — called in both embedded and standalone mode:
 *     navigate("manual", onClick)
 *
 *   An object with per-mode keys — called based on current mode:
 *     navigate("manual", { embedded: onEmbeddedClick, standalone: onStandaloneClick })
 *   Either key is optional; the other is used as fallback if one is missing.
 *
 * --- Options argument ---
 *   { newTab: true }   Opens the destination in a new browser tab.
 *   { newTab: false }  Opens in the same tab (overrides the default for "link:").
 *   { before: fn }     Called once immediately before navigation executes (all modes). Receives the resolved target string as its first argument.
 *   Defaults: "link:" → new tab; "sp:" / "sb:" → same tab.
 *
 * --- Single target (same action in both modes) ---
 *   navigate("sp:/schedule")
 *   navigate("sb:/courses")
 *   navigate("link:https://quiz.codeyoung.com")
 *   navigate("sp:/schedule", null, { newTab: true })
 *   navigate("sb:/courses", null, { newTab: true })
 *   navigate("link:https://...", null, { newTab: false })
 *   navigate("manual", onViewSchedule)
 *   navigate("manual", { embedded: onEmbeddedClick, standalone: onStandaloneClick })
 *
 * --- Dual target (different action per mode) ---
 *   navigate(["sp:/schedule", "sb:/viewSchedule"])
 *   navigate(["sp:/schedule", "link:https://..."])
 *   navigate(["sp:/schedule", "manual"], { standalone: onStandaloneClick })
 *   navigate(["sp:/schedule", "sb:/viewSchedule"], null, { newTab: true })
 *
 * When an array is given the hook picks the best match for the current mode.
 * Embedded mode priority : sp: › sb: › link: › manual
 * Standalone mode priority: sb: › link: › manual › sp:
 * Array order does not matter — resolution is always type-based.
 *
 * @returns {{ navigate: function(target: string|string[], handler?: function|{embedded?: function, standalone?: function}, options?: { newTab?: boolean, before?: function }): void }}
 */
function useSPNavigate() {
  const history = useHistory();

  /**
   * Resolves and executes navigation.
   * @param {string|string[]} target - Route target or array of per-mode targets.
   * @param {function|{embedded?: function, standalone?: function}} [handler] - Required when the resolved target is "manual".
   * @param {{ newTab?: boolean, before?: function }} [options]
   */
  function navigate(target, handler, options = {}) {
    const { newTab, before } = options;
    const onNavigateToPlatform = getNavigateToPlatform();
    const isEmbedded = typeof onNavigateToPlatform === "function";

    const resolved = resolveTarget(target, isEmbedded);
    if (!resolved || typeof resolved !== "string") return;

    if (typeof before === "function") before(resolved);

    if (resolved.startsWith("link:")) {
      const url = resolved.slice("link:".length);
      if (newTab === false) {
        window.location.href = url;
      } else {
        window.open(url, "_blank", "noopener,noreferrer");
      }
      return;
    }

    if (resolved.startsWith("sp:")) {
      const path = resolved.slice("sp:".length);
      if (!isEmbedded) {
        // Standalone: the portal is another origin and Sandbox's router has none of
        // its routes — pushing there falls through to the /home redirect. Hand the
        // session across and leave for the portal instead.
        redirectToStudentPortal(path, { newTab: !!newTab });
      } else if (newTab) {
        // window.location is the portal page — construct its hash URL.
        window.open(window.location.origin + "/#" + path, "_blank", "noopener,noreferrer");
      } else {
        onNavigateToPlatform(path);
      }
      return;
    }

    if (resolved.startsWith("sb:")) {
      const path = resolved.slice("sb:".length);
      if (newTab) {
        // In embedded mode __SANDBOX_PUBLIC_PATH__ holds the sandbox origin.
        // In standalone mode window.location.origin IS the sandbox origin.
        const sandboxBase =
          window.__SANDBOX_PUBLIC_PATH__ || window.location.origin + "/";
        window.open(sandboxBase + "#" + path, "_blank", "noopener,noreferrer");
      } else {
        history.push(path);
      }
      return;
    }

    if (resolved === "manual") {
      const fn = resolveManualHandler(handler, isEmbedded);
      if (typeof fn === "function") {
        fn();
      } else {
        console.warn("[useSPNavigate] 'manual' target used but no handler was provided.");
      }
      return;
    }

    console.warn("[useSPNavigate] Unrecognised target format:", resolved);
  }

  return { navigate };
}

/**
 * Picks the most appropriate target from an array for the current mode.
 * Falls back to the first element if no preferred type is found.
 * @param {string|string[]} target
 * @param {boolean} isEmbedded
 * @returns {string}
 */
function resolveTarget(target, isEmbedded) {
  if (!Array.isArray(target)) return target;

  if (isEmbedded) {
    return (
      target.find((t) => t.startsWith("sp:")) ||
      target.find((t) => t.startsWith("sb:")) ||
      target.find((t) => t.startsWith("link:")) ||
      target.find((t) => t === "manual") ||
      target[0]
    );
  }

  return (
    target.find((t) => t.startsWith("sb:")) ||
    target.find((t) => t.startsWith("link:")) ||
    target.find((t) => t === "manual") ||
    target.find((t) => t.startsWith("sp:")) ||
    target[0]
  );
}

/**
 * Resolves the correct handler function for a "manual" target.
 * Accepts either a plain function (used for both modes) or an object
 * with optional `embedded` and `standalone` keys for per-mode handlers.
 * @param {function|{embedded?: function, standalone?: function}} handler
 * @param {boolean} isEmbedded
 * @returns {function|null}
 */
function resolveManualHandler(handler, isEmbedded) {
  if (typeof handler === "function") return handler;
  if (handler && typeof handler === "object") {
    return isEmbedded
      ? (handler.embedded || handler.standalone || null)
      : (handler.standalone || handler.embedded || null);
  }
  return null;
}

export default useSPNavigate;
